import React, { useState } from "react";
import { getValue } from "../utils";
import ConfirmDialog from "../../ConfirmDialog/ConfirmDialog";
import DynamicButton from "../../DynamicButton/DynamicButton";
import "./TableRow.css";
import PasswordConfirmDialog from "./PasswordConfirmDialog";

const stopPropagation = (event) => event.stopPropagation();

const isStatusColumn = (column) => {
  const field = String(column?.field || "").toLowerCase();
  const label = String(column?.label || "").toLowerCase();

  return field === "status" || field.endsWith(".status") || label === "status";
};

const getStatusClass = (value) => {
  const status = String(value || "")
    .trim()
    .toLowerCase();

  if (status === "open") {
    return "safety-table-row__status--open";
  }

  if (status === "closed") {
    return "safety-table-row__status--closed";
  }

  return "safety-table-row__status--default";
};

const renderCellValue = (value) => {
  if (!value) return "—";

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "object"
          ? item?.name || item?.roleName || item?.projectName || item?._id
          : String(item),
      )
      .join(", ");
  }

  if (typeof value === "object") {
    return (
      value.name ||
      value.roleName ||
      value.projectName ||
      value._id ||
      JSON.stringify(value)
    );
  }

  return String(value);
};

const getFileName = (url) => {
  try {
    return decodeURIComponent(url.split("/").pop().split("?")[0]);
  } catch {
    return "download";
  }
};

const TableRow = ({
  row,
  index,
  columns,
  editingRow,
  handleInputChange,
  handleDelete,
  handleEdit,
  selectedRows,
  toggleRowSelect,
  onRowClick,
  config = {},
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const isSelected = selectedRows.includes(row._id);
  const isEditing = editingRow === index;
  const visibleColumns = columns.slice(0, 6);

  const requestDelete = (event) => {
    stopPropagation(event);
    setShowConfirm(true);
  };

  // const confirmDelete = () => {
  //   setShowConfirm(false);
  //   handleDelete(row._id);
  // };
  const confirmDelete = async (currentPassword) => {
    // Only close after the backend verifies the password and deletes the record.
    await handleDelete(row._id, { currentPassword });
    setShowConfirm(false);
  };

  const cancelDelete = () => {
    setShowConfirm(false);
  };

  const handleDownload = () => {
    const fileColumns = columns.filter((column) => column.type === "file");

    if (!fileColumns.length) {
      alert("No file column found");
      return;
    }

    let urls = [];

    fileColumns.forEach((fileColumn) => {
      const fileUrl = getValue(row, fileColumn.field);

      if (!fileUrl) return;

      if (Array.isArray(fileUrl)) {
        urls = [...urls, ...fileUrl];
      } else {
        urls.push(fileUrl);
      }
    });

    if (!urls.length) {
      alert("No file available for download");
      return;
    }

    urls.forEach((url) => {
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        url,
      )}&embedded=true`;

      const link = document.createElement("a");
      link.href = viewerUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.setAttribute("download", getFileName(url));

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (
    <>
      <tr
        className={`safety-table-row ${
          isSelected ? "safety-table-row--selected" : ""
        }`}
        onClick={() => onRowClick?.(row)}
      >
        <td
          className="safety-table-row__checkbox-cell"
          onClick={stopPropagation}
        >
          <input
            className="safety-table-row__checkbox"
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleRowSelect(row._id)}
            aria-label="Select row"
          />
        </td>

        {visibleColumns.map((column, columnIndex) => {
          const rawValue = getValue(row, column.field);
          const displayValue = renderCellValue(rawValue);
          const statusColumn = isStatusColumn(column);

          return (
            <td key={columnIndex} className="safety-table-row__cell">
              {isEditing ? (
                <input
                  type="text"
                  className="safety-table-row__input"
                  value={rawValue || ""}
                  onChange={(event) =>
                    handleInputChange(event, index, column.field)
                  }
                  onClick={stopPropagation}
                />
              ) : statusColumn ? (
                <span
                  className={`safety-table-row__status ${getStatusClass(rawValue)}`}
                  title={displayValue}
                >
                  <span className="safety-table-row__status-dot" />
                  {displayValue}
                </span>
              ) : (
                <span className="safety-table-row__value" title={displayValue}>
                  {displayValue}
                </span>
              )}
            </td>
          );
        })}

        <td
          className="safety-table-row__actions-cell"
          onClick={stopPropagation}
        >
          <div className="safety-table-row__actions">
            <DynamicButton
              icon="fa-edit"
              size="md"
              variant="primary"
              onClick={() => handleEdit(row._id)}
              title="Edit this record"
            />

            <DynamicButton
              icon="fa-trash"
              size="md"
              variant="danger"
              onClick={requestDelete}
              title="Delete this record"
            />

            {config.DownloadButton === "true" && (
              <DynamicButton
                icon="fa-download"
                size="md"
                variant="outline"
                onClick={handleDownload}
                title="Download file(s)"
              />
            )}
          </div>
        </td>
      </tr>

      {/* <ConfirmDialog
        open={showConfirm}
        title="Delete this record?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        variant="danger"
      /> */}

      <PasswordConfirmDialog
        open={showConfirm}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default TableRow;
